import { useEffect } from "react";

import { Controller, useFormContext } from "react-hook-form";

import { useWorkspaceUsers } from "src/api/taktile/queries";
import { RequiredAsterisk } from "src/base-components/RequiredAsterisk";
import { Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { Switch } from "src/base-components/Switch";
import { Avatar } from "src/design-system/Avatar";
import { RadioLabel } from "src/flowReview/ReviewProcessPane";
import { DecisionFlowInputs } from "src/flowsOverview/types";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { getUserName } from "src/utils/user";

type ConfigOptionProps = {
  dataLoc?: string;
  title: string;
  description: string;
  onChange: (value: boolean) => void;
  value: boolean;
};

const ConfigOption: React.FC<ConfigOptionProps> = ({
  dataLoc,
  title,
  description,
  onChange,
  value,
}) => {
  return (
    <div className="flex gap-x-2">
      <div>
        <h3 className="mb-0.5 font-inter-semibold-13px text-gray-800">
          {title}
        </h3>
        <p className="font-inter-normal-12px text-gray-500">{description}</p>
      </div>
      <Switch dataLoc={dataLoc} enabled={value} onChange={onChange} />
    </div>
  );
};

export const FlowReviewConfigEditor: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { data: users } = useWorkspaceUsers(orgId, wsId, {
    include_deactivated: false,
  });
  const { watch, trigger, clearErrors } = useFormContext<DecisionFlowInputs>();

  const usersOptions =
    users?.map((user) => ({
      key: user.id,
      searchText: getUserName(user),
      value: (
        <span className="flex items-center gap-x-2">
          <Avatar user={user} />
          {getUserName(user)}
        </span>
      ),
    })) ?? [];

  const defaultReviewers = watch("reviewConfig.default_reviewer_list");
  const requiresReview = watch("reviewConfig.requires_review");

  // Trigger validation on reviewers when requiresReview is enabled - need to be done manually
  useEffect(() => {
    if (requiresReview) {
      trigger("reviewConfig.default_reviewer_list");
    } else {
      // Clear validation errors when requiresReview is disabled to avoid showing the error message when the user disables the review requirement
      clearErrors("reviewConfig.default_reviewer_list");
    }
  }, [requiresReview, trigger, clearErrors]);

  return (
    <div className="mt-4 flex flex-col gap-y-4 border-t border-t-gray-100 pt-4">
      <Controller
        defaultValue={false}
        name="reviewConfig.requires_review"
        render={({ field }) => (
          <ConfigOption
            dataLoc="require-review-toggle"
            description="When enabled, versions of this Decision Flow will require approval from reviewers before they can be published."
            title="Require review before publishing"
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      {requiresReview && (
        <Controller
          defaultValue={false}
          name="reviewConfig.requires_independent_review"
          render={({ field }) => (
            <ConfigOption
              dataLoc="require-independent-review-toggle"
              description="A user who has made changes to a version, including creating or duplicating it, cannot approve it for publishing. This prevents bias and ensures an objective review process."
              title="Contributor to a version cannot approve it for publishing"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      )}
      {requiresReview && (
        <div className="flex flex-col">
          <h3 className="mb-1 font-inter-semibold-13px text-gray-800">
            Select reviewers <RequiredAsterisk />
          </h3>
          <p className="mb-2 font-inter-normal-12px text-gray-500">
            Approval from selected reviewer(s) is needed to publish the
            versions.
          </p>
          <Controller
            defaultValue={[] as string[]}
            name="reviewConfig.default_reviewer_list"
            render={(props) => (
              <Select
                dataLoc="reviewers-select"
                errored={
                  props.fieldState.isDirty && Boolean(props.fieldState.error)
                }
                options={usersOptions}
                placeholder="Select reviewer(s)"
                searchPlaceholder="Search user..."
                value={props.field.value}
                clearAfterClose
                multiple
                searchable
                onChange={props.field.onChange}
              />
            )}
            rules={{ required: true, minLength: 1 }}
          />
        </div>
      )}
      {requiresReview && defaultReviewers && defaultReviewers.length > 1 && (
        <Controller
          defaultValue={true}
          name="reviewConfig.require_all_reviewers"
          render={({ field }) => (
            <SimpleRadioGroup
              orientation="vertical"
              value={`${field.value}`}
              onValueChange={(value) => field.onChange(value === "true")}
            >
              <SimpleRadioGroup.Item
                label={
                  <RadioLabel
                    description="Allow users to publish versions only if all selected reviewers have given their approval."
                    label="All reviewers"
                  />
                }
                value="true"
                alignTop
              />
              <SimpleRadioGroup.Item
                label={
                  <RadioLabel
                    description="Allow users to publish versions if approval is received from any of the selected reviewers."
                    label="At least one reviewer"
                  />
                }
                value="false"
                alignTop
              />
            </SimpleRadioGroup>
          )}
        />
      )}
    </div>
  );
};
